import { RecentSearchListItem } from '@/src/hooks/srp/recentSearch'
import { TranslationFunction } from '@/src/types/translations'
import { Box, Tab, Tabs, alpha } from '@mui/material'
import React from 'react'
import SearchForm from '../searchForm'
import { TabPanel, a11yProps } from '../tabPanel'

interface FlightSearchTabsProps {
    t: TranslationFunction<'header'>
    tabValue: number
    onTabChange: (event: React.SyntheticEvent, newValue: number) => void
    isMobile: boolean
    serverRecentSearches: RecentSearchListItem[]
    hideMulticity?: boolean
}

export const FlightSearchTabs: React.FC<FlightSearchTabsProps> = ({
    t,
    tabValue,
    onTabChange,
    isMobile,
    serverRecentSearches,
    hideMulticity
}) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
            {/* Tabs Container - Separate white box */}
            <Box
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'common.white',
                    borderRadius: 5,
                    border: 1,
                    borderColor: 'grey.300',
                    py: 1.5,
                    px: 2.5,
                    width: 'fit-content',
                    position: 'relative',
                    zIndex: 2
                }}
            >
                <Tabs
                    value={tabValue}
                    onChange={onTabChange}
                    aria-label="flight search tabs"
                    sx={{
                        minHeight: 'unset',
                        '& .MuiTabs-flexContainer': {
                            gap: 0
                        },
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: 'grey.600',
                            px: 3,
                            py: 1,
                            minWidth: 'unset',
                            minHeight: 'unset',
                            borderRadius: 1,
                            position: 'relative',
                            transition: 'all 0.2s ease',
                            '&.Mui-selected': {
                                fontWeight: 600,
                                color: 'primary.main',
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                border: theme => `1.5px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                borderRadius: 5,
                                zIndex: 1
                            },
                            '&:not(:last-child)::after': {
                                content: '""',
                                position: 'absolute',
                                right: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '1px',
                                height: '20px',
                                bgcolor: 'grey.300',
                                zIndex: 0
                            },
                            '&.Mui-selected::after': {
                                display: 'none'
                            }
                        },
                        '& .MuiTabs-indicator': {
                            display: 'none'
                        }
                    }}
                >
                    <Tab label={t('flights')} {...a11yProps(0)} />
                    <Tab label={t('hotels')} {...a11yProps(1)} />
                    <Tab label={`${t('flights')} + ${t('hotels')}`} {...a11yProps(2)} />
                </Tabs>
            </Box>

            {/* Search Form Container - Separate white box with top overlapped by tabs */}
            <Box
                sx={{
                    bgcolor: 'common.white',
                    px: 3,
                    pt: 2,
                    pb: 3,
                    borderRadius: 2,
                    border: 2,
                    borderColor: 'grey.100',
                    width: '100%',
                    position: 'relative',
                    zIndex: 1,
                    mt: '-12px' // Negative margin to create 20% overlap with tabs bottom (approximately 20% of tabs container height)
                }}
            >
                <TabPanel value={tabValue} index={0}>
                    <SearchForm isMobile={isMobile} serverRecentSearches={serverRecentSearches} hideMulticity={hideMulticity} />
                </TabPanel>
            </Box>
        </Box>
    )
}
